import { ReactElement, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { StructuralPlanningElementFormHeader } from '@structuralPlanningComponents'
import { v4 } from 'uuid'
import { Stack } from '@mui/material'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { useIsMutating } from '@tanstack/react-query'
import { DeleteButton, SaveButton } from '@ui/actions'
import { Form } from '@ui/forms'
import { useElementLabel } from '@editorHooks'
import { CollapsibleGeometryBoxSection } from '@editorComponents'
import FormFields from './components/FormFields'
import WallFormSceneMarker from './components/WallFormSceneMarker'
import { applyFormValuesToWall } from './components/WallFormSceneMarker/utils'
import { wallFormV2Schema } from './schema'

interface Props {
  handleSubmit: (wall: ShapeObject) => void
  isSubmitting?: boolean
  handleDelete: () => void
  isDeleting?: boolean
  wall: ShapeObject
  isPlacementEditable?: boolean
  isSaveable?: boolean
  onClose?: () => void
}

const WallFormV2 = ({
  handleDelete,
  handleSubmit,
  isSubmitting,
  isDeleting,
  wall,
  isSaveable = false,
  isPlacementEditable = true,
  onClose,
}: Props): ReactElement => {
  const getLabel = useElementLabel()
  const label = getLabel(wall.guid)

  const { projectId } = useParams()

  // Disable the fields while any wall geometry update for this project is running (e.g. the IFC
  // importer's auto-save on drag), so the form cannot be edited mid-save.
  const isUpdatingWall = useIsMutating(['updateWall', projectId]) > 0

  const defaultValues = useMemo(() => {
    const [start, end] = wall.shape.points

    return {
      guid: wall.guid,
      start: {
        x: start.x,
        y: start.y,
        z: start.z,
      },
      end: {
        x: end.x,
        y: end.y,
        z: end.z,
      },
      placement: wall.placement ?? 'External',
    }
  }, [wall])

  const [formStateRandomId, setFormStateRandomId] = useState(v4())

  return (
    <Form
      key={`${wall.guid}-${formStateRandomId}`}
      onSubmit={values => {
        const submittedWall = applyFormValuesToWall(
          wall,
          new ImmutableVector3(values.start.x, values.start.y, values.start.z),
          new ImmutableVector3(values.end.x, values.end.y, values.end.z),
          values.placement,
        )
        handleSubmit(submittedWall)
        setFormStateRandomId(v4())
      }}
      validationSchema={wallFormV2Schema}
      defaultValues={defaultValues}
    >
      <WallFormSceneMarker wallGuid={wall.guid} />
      <Stack direction="column" spacing={1}>
        <StructuralPlanningElementFormHeader
          elementGuid={wall.guid}
          label={label}
          variant="h6"
          onClose={onClose}
        />

        <CollapsibleGeometryBoxSection label="Geometrie" defaultExpanded={true}>
          <Stack direction="column" spacing={1}>
            <FormFields
              wall={wall}
              isPlacementEditable={isPlacementEditable}
              disabled={isUpdatingWall}
            />

            <Stack direction="row" justifyContent="end" spacing={1}>
              <SaveButton
                type="submit"
                loading={isSubmitting}
                fullWidth
                disabled={isDeleting || !isSaveable || isUpdatingWall}
                data-cy="btn-wall-geometry-form-save"
              >
                Speichern
              </SaveButton>
              <DeleteButton
                onClick={handleDelete}
                size="small"
                fullWidth
                loading={isDeleting}
                disabled={isSubmitting || isUpdatingWall}
                data-cy="delete-wall-btn"
              >
                Löschen
              </DeleteButton>
            </Stack>
          </Stack>
        </CollapsibleGeometryBoxSection>
      </Stack>
    </Form>
  )
}

export default WallFormV2
